import { ThemeEvents } from '@theme/events';

class GlxFlavorPicker extends HTMLElement {
  constructor() {
    super();

    this.sectionId = null;
    this.activeItem = null;
    this.abortController = null;
    this.cache = new Map();
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;

    const currentProductId = String(
      this.dataset.currentProductId || ''
    );

    const items = this.querySelectorAll(
      '[data-glx-flavor]'
    );

    items.forEach((item) => {
      const selected =
        String(item.dataset.productId) === currentProductId;

      item.classList.toggle(
        'glx-flavor-picker__item--selected',
        selected
      );

      item.setAttribute(
        'aria-pressed',
        selected ? 'true' : 'false'
      );

      if (selected) {
        this.activeItem = item;
        this.applyActiveColors(item);
        this.updateQuantityPackImages(item);
      }
    });

    this.addEventListener(
      'click',
      this.onClick,
      true
    );
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);

    if (this.abortController) {
      this.abortController.abort();
    }
  }

  onClick = async (event) => {
    console.log('[GLX] clic détecté', event.target);

    const item = event.target.closest('[data-glx-flavor]');

    console.log('[GLX] bouton trouvé', item);

    if (!item || !this.contains(item)) return;

    console.log('[GLX] changement vers', {
      product: item.dataset.productTitle,
      handle: item.dataset.productHandle,
      variantId: item.dataset.variantId
    });

    if (item.disabled) return;

    if (
      item.classList.contains(
        'glx-flavor-picker__item--selected'
      )
    ) {
      return;
    }

    await this.selectProduct(item);
  };

  async selectProduct(item) {
    const productHandle = item.dataset.productHandle;
    const productId = item.dataset.productId;
    const variantId = item.dataset.variantId;

    if (!productHandle || !this.sectionId) {
      console.warn(
        '[GLX Flavor Picker] Données produit manquantes',
        {
          productHandle,
          productId,
          variantId,
          sectionId: this.sectionId,
        }
      );

      return;
    }

    this.setLoading(true);

    try {
      /*
       * 1. Chargement du HTML du nouveau produit
       * sans navigation.
       */
      const html = await this.fetchProductSection(
        productHandle
      );

      if (!html) {
        throw new Error(
          `Impossible de charger le produit ${productHandle}`
        );
      }

      /*
       * 2. Mise à jour visuelle de la saveur.
       */
      this.setSelectedItem(item);

      /*
       * 3. Mise à jour du titre produit.
       */
      this.updateProductTitle(html);

      /*
       * 4. Événement GLX.
       *
       * On pourra l'utiliser ensuite pour :
       * - Kaching
       * - selling plans
       * - abonnement / achat unique
       * - prix
       * - autres composants GLX
       */
      this.dispatchEvent(
        new CustomEvent('glx:product-change', {
          bubbles: true,
          detail: {
            productId,
            variantId,
            handle: productHandle,
            item,
            html,
          },
        })
      );

      /*
       * 5. Événement natif du thème.
       *
       * La galerie et le quantity-break existants
       * savent déjà réagir à variantUpdated.
       */
      const productSection = this.closest(
        '.shopify-section, dialog'
      );

      if (productSection) {
        productSection.dispatchEvent(
          new CustomEvent(
            ThemeEvents.variantUpdated,
            {
              bubbles: true,
              detail: {
                data: {
                  html,
                },
              },
            }
          )
        );
      }

      /*
       * 6. Synchronisation de la variante Shopify
       * utilisée réellement par le formulaire.
       */
      this.syncProductFormVariant(variantId);

      /*
      * Le quantity-break réagit lui aussi à variantUpdated
      * et peut remettre l'image produit par défaut.
      *
      * On réapplique donc les images propres à la saveur
      * une fois les composants du thème mis à jour.
      */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.updateQuantityPackImages(item);
        });
      });

    } catch (error) {
      /*
       * Une AbortError peut arriver si l'utilisateur
       * clique très rapidement sur plusieurs saveurs.
       */
      if (error.name !== 'AbortError') {
        console.error(
          '[GLX Flavor Picker] Erreur changement produit :',
          error
        );
      }
    } finally {
      this.setLoading(false);
    }
  }

  async fetchProductSection(handle) {
    /*
     * Cache :
     * si on a déjà visité cette saveur,
     * pas besoin de refaire une requête réseau.
     */
    if (this.cache.has(handle)) {
      return this.cache
        .get(handle)
        .cloneNode(true);
    }

    /*
     * Annulation de la requête précédente
     * si nécessaire.
     */
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    const url =
      `/products/${encodeURIComponent(handle)}` +
      `?section_id=${encodeURIComponent(this.sectionId)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/html',
      },
      signal: this.abortController.signal,
    });

    if (!response.ok) {
      throw new Error(
        `Erreur HTTP ${response.status}`
      );
    }

    const responseText = await response.text();

    const parser = new DOMParser();

    const html = parser.parseFromString(
      responseText,
      'text/html'
    );

    /*
     * On conserve le document original en cache.
     */
    this.cache.set(handle, html);

    return html.cloneNode(true);
  }

  setSelectedItem(selectedItem) {
    /*
     * IMPORTANT :
     * on requête les items à chaque fois.
     *
     * On ne garde volontairement pas une liste
     * créée dans connectedCallback, car Shopify
     * peut modifier les sous-blocks dynamiquement.
     */
    const items = this.querySelectorAll(
      '[data-glx-flavor]'
    );

    items.forEach((item) => {
      const selected = item === selectedItem;

      item.classList.toggle(
        'glx-flavor-picker__item--selected',
        selected
      );

      item.setAttribute(
        'aria-pressed',
        selected ? 'true' : 'false'
      );
    });

    this.activeItem = selectedItem;
    this.applyActiveColors(selectedItem);
    this.updateQuantityPackImages(selectedItem);
  }
  applyActiveColors(item) {
    if (!item) return;

    const productSection = this.closest(
      '.shopify-section, dialog'
    );

    if (!productSection) return;

    const borderColor =
      item.dataset.accentBorderColor;

    const backgroundColor =
      item.dataset.accentBackgroundColor;

    const subscriptionBackground =
      item.dataset.accentSubscriptionBackground;

    const savingPillColor =
      item.dataset.savingPillColor;


    if (borderColor) {
      productSection.style.setProperty(
        '--glx-active-color',
        borderColor
      );
    }

    if (backgroundColor) {
      productSection.style.setProperty(
        '--glx-active-background',
        backgroundColor
      );
    }

    if (subscriptionBackground) {
      productSection.style.setProperty(
        '--glx-active-subscription-background',
        subscriptionBackground
      );
    }

    if (savingPillColor) {
      productSection.style.setProperty(
        '--glx-saving-pill-color',
        savingPillColor
      );
    }
  }
  updateQuantityPackImages(item) {
    if (!item) return;

    const productSection = this.closest(
      '.shopify-section, dialog'
    );

    if (!productSection) return;

    const packImages = {
      1: item.getAttribute('data-pack-1-image'),
      2: item.getAttribute('data-pack-2-image'),
      3: item.getAttribute('data-pack-3-image'),
    };

    Object.entries(packImages).forEach(
      ([quantity, imageUrl]) => {
        if (!imageUrl) {
          console.warn(
            `[GLX] aucune image définie pour le pack ${quantity}`
          );
          return;
        }

        const quantityItem =
          productSection.querySelector(
            `quantity-break-item[data-quantity="${quantity}"]`
          );

        if (!quantityItem) {
          console.warn(
            `[GLX] pack quantité ${quantity} introuvable`
          );
          return;
        }

        const image = quantityItem.querySelector(
          '[data-ref="quantity-break-image"]'
        );

        if (!image) {
          console.warn(
            `[GLX] image du pack ${quantity} introuvable`
          );
          return;
        }

        image.removeAttribute('srcset');
        image.removeAttribute('sizes');

        image.src = imageUrl;

        console.log(
          `[GLX] image pack ${quantity} mise à jour`,
          imageUrl
        );
      }
    );
  }
  updateProductTitle(html) {
    if (!html) return;

    const productSection = this.closest(
      '.shopify-section, dialog'
    );

    if (!productSection) return;

    /*
     * Block titre actuellement affiché.
     */
    const currentTitleBlock =
      productSection.querySelector(
        '.glx-product-title'
      );

    /*
     * Même block dans le HTML du produit
     * fraîchement chargé.
     */
    const newTitleBlock =
      html.querySelector(
        '.glx-product-title'
      );

    if (!currentTitleBlock || !newTitleBlock) {
      return;
    }

    /*
     * On ne remplace que le contenu afin de garder
     * les classes / styles / attributs Shopify
     * du block actuellement monté.
     */
    currentTitleBlock.innerHTML =
      newTitleBlock.innerHTML;
  }

  syncProductFormVariant(variantId) {
    if (!variantId) return;

    const productSection = this.closest(
      '.shopify-section, dialog'
    );

    if (!productSection) return;

    /*
     * Mise à jour du véritable input Shopify :
     *
     * <input name="id" value="VARIANT_ID">
     */
    productSection
      .querySelectorAll(
        'product-form form input[name="id"]'
      )
      .forEach((input) => {
        input.value = variantId;

        input.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );
      });
  }

  setLoading(loading) {
    this.classList.toggle(
      'glx-flavor-picker--loading',
      loading
    );

    this.setAttribute(
      'aria-busy',
      loading ? 'true' : 'false'
    );
  }
}

if (!customElements.get('glx-flavor-picker')) {
  customElements.define(
    'glx-flavor-picker',
    GlxFlavorPicker
  );
}